import {
  fetchLlmGatewayConfiguration,
  fetchLlmGatewayKeyById,
  formatCurrency,
  revokeLlmGatewayKey,
  selectLlmGatewayConfiguration,
  selectLlmGatewayKeyById,
  updateLlmGatewayKey,
} from "@app/deploy";
import {
  currentMonthWindow,
  fetchLlmGatewayUsage,
  selectLlmGatewayUsage,
} from "@app/llm-gateway";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import type { DeployLlmGatewayKey } from "@app/types";
import { useDebouncedValue, useValidator } from "@app/ui/hooks";
import { handleValidator } from "@app/validator";
import { type FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Box,
  BoxGroup,
  Button,
  CheckBox,
  FormGroup,
  FormGroupFeedback,
  Group,
  IconAlertTriangle,
  IconTrash,
  Input,
  Loading,
} from "../shared";
import {
  LlmGatewayKeySpendLimitField,
  spendLimitValidator,
} from "../shared/llm-gateway-key/spend-limit-field";

interface SettingsFormData {
  handle: string;
  spendLimit: number | undefined;
}

const CONFIRMATION_DELAY_MS = 250;

/** A key whose limit is at or below what it has spent stops working right away. */
const blocksKey = (
  limit: number | undefined,
  savedLimit: number | undefined,
  spend: number,
) => limit !== undefined && limit !== savedLimit && limit <= spend;

const KeySettingsForm = ({
  llmGatewayKey,
  orgSpendLimit,
}: { llmGatewayKey: DeployLlmGatewayKey; orgSpendLimit: number }) => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const isRevoked = llmGatewayKey.status === "REVOKED";

  const savedSpendLimit =
    llmGatewayKey.spendLimit === null ? undefined : llmGatewayKey.spendLimit;
  const [handle, setHandle] = useState(llmGatewayKey.handle);
  const [spendLimit, setSpendLimit] = useState(savedSpendLimit);
  useEffect(() => {
    setHandle(llmGatewayKey.handle);
    setSpendLimit(savedSpendLimit);
  }, [llmGatewayKey.handle, savedSpendLimit]);

  const validators = {
    handle: (data: SettingsFormData) => handleValidator(data.handle),
    spendLimit: (data: SettingsFormData) =>
      spendLimitValidator(data.spendLimit, orgSpendLimit),
  };
  const [errors, validate] = useValidator<SettingsFormData, typeof validators>(
    validators,
  );

  const [usageWindow] = useState(currentMonthWindow);
  const usageLoader = useQuery(
    fetchLlmGatewayUsage({
      orgId,
      llmKeyId: llmGatewayKey.id,
      ...usageWindow,
    }),
  );
  const usage = useSelector((s) =>
    selectLlmGatewayUsage(s, { orgId, llmKeyId: llmGatewayKey.id }),
  );
  const isUsageKnown = usage.id !== "";
  const spendThisMonth = isUsageKnown ? usage.spend : 0;
  // A new limit can't be judged against spend that hasn't loaded.
  const isCheckingUsage = spendLimit !== savedSpendLimit && !isUsageKnown;

  // The limit that was confirmed, so editing it again asks again.
  const [confirmedLimit, setConfirmedLimit] = useState<number | undefined>();
  const isAwaitingConfirmation =
    blocksKey(spendLimit, savedSpendLimit, spendThisMonth) &&
    confirmedLimit !== spendLimit;
  // Trails the field so the checkbox doesn't flash while the amount is typed.
  const settledLimit = useDebouncedValue(spendLimit, CONFIRMATION_DELAY_MS);
  const limitToConfirm = blocksKey(
    settledLimit,
    savedSpendLimit,
    spendThisMonth,
  )
    ? settledLimit
    : undefined;

  const action = updateLlmGatewayKey({
    id: llmGatewayKey.id,
    orgId,
    handle,
    spendLimit: spendLimit === undefined ? null : spendLimit,
  });
  const loader = useLoader(action);
  useLoaderSuccess(loader, () => setConfirmedLimit(undefined));

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isAwaitingConfirmation || isCheckingUsage) return;
    if (!validate({ handle, spendLimit })) return;
    dispatch(action);
  };

  const onCancel = () => {
    setHandle(llmGatewayKey.handle);
    setSpendLimit(savedSpendLimit);
    setConfirmedLimit(undefined);
    validate({ handle: llmGatewayKey.handle, spendLimit: savedSpendLimit });
  };

  const isUnchanged =
    handle === llmGatewayKey.handle && spendLimit === savedSpendLimit;

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      {isRevoked ? (
        <Banner variant="info">
          This key has been revoked, so its settings can't be changed.
        </Banner>
      ) : null}

      <FormGroup
        label="Handle"
        htmlFor="key-handle"
        feedbackMessage={errors.handle}
        feedbackVariant={errors.handle ? "danger" : "default"}
      >
        <Input
          id="key-handle"
          name="key-handle"
          value={handle}
          onChange={(e) => setHandle(e.currentTarget.value)}
          maxLength={64}
          disabled={isRevoked}
        />
      </FormGroup>

      <LlmGatewayKeySpendLimitField
        id="key-spend-limit"
        value={spendLimit}
        onChange={setSpendLimit}
        orgSpendLimit={orgSpendLimit}
        error={errors.spendLimit}
        disabled={isRevoked}
      />

      {isCheckingUsage && !usageLoader.isError ? (
        <FormGroupFeedback variant="info">
          Checking this key's usage…
        </FormGroupFeedback>
      ) : null}
      {isCheckingUsage && usageLoader.isError ? (
        <Banner variant="error">
          We couldn't load this key's usage, so its spend limit can't be changed
          right now.
        </Banner>
      ) : null}

      {limitToConfirm === undefined ? null : (
        <CheckBox
          checked={confirmedLimit === limitToConfirm}
          onChange={(e) =>
            setConfirmedLimit(
              e.currentTarget.checked ? limitToConfirm : undefined,
            )
          }
          labelClassName="ml-3 text-red-500"
          label={`I understand that by setting this key's spend limit to ${formatCurrency(limitToConfirm)}, my key will immediately stop working until the next billing cycle, or until I increase its spend limit to be above its current spend of ${formatCurrency(spendThisMonth)}.`}
        />
      )}

      <BannerMessages {...loader} />

      <Group variant="horizontal" size="sm">
        <Button
          type="submit"
          className="w-40 semibold"
          isLoading={loader.isLoading}
          disabled={
            isRevoked ||
            isUnchanged ||
            isAwaitingConfirmation ||
            isCheckingUsage
          }
        >
          Save Changes
        </Button>
        <Button variant="white" onClick={onCancel} disabled={isRevoked}>
          Cancel
        </Button>
      </Group>
    </form>
  );
};

const RevokeLlmGatewayKey = ({
  llmGatewayKey,
}: { llmGatewayKey: DeployLlmGatewayKey }) => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const [confirm, setConfirm] = useState("");
  const action = revokeLlmGatewayKey({ id: llmGatewayKey.id, orgId });
  const loader = useLoader(action);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(action);
  };

  return (
    <form onSubmit={onSubmit}>
      <h1 className="text-lg text-red-500 font-semibold flex items-center gap-2 mb-4">
        <IconAlertTriangle color="#AD1A1A" />
        Revoke Key
      </h1>

      <Group>
        <p>
          This will permanently revoke <strong>{llmGatewayKey.handle}</strong>.
          Any applications using this key will immediately lose access to the
          LLM Gateway. This action cannot be undone. If you want to proceed,
          type <strong>{llmGatewayKey.handle}</strong> below to continue.
        </p>

        <BannerMessages {...loader} />

        <Group variant="horizontal" size="sm" className="items-center">
          <Input
            className="flex-1"
            name="revoke-confirm"
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            id="revoke-confirm"
          />
          <Button
            variant="delete"
            disabled={confirm !== llmGatewayKey.handle}
            isLoading={loader.isLoading}
            className="w-70"
            type="submit"
          >
            <IconTrash color="#FFF" className="mr-2" />
            Revoke Key
          </Button>
        </Group>
      </Group>
    </form>
  );
};

export const LlmGatewayKeySettingsPage = () => {
  const { id = "" } = useParams();
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchLlmGatewayConfiguration({ orgId }));
  useQuery(fetchLlmGatewayKeyById({ id, orgId }));
  const config = useSelector(selectLlmGatewayConfiguration);
  const llmGatewayKey = useSelector((s) => selectLlmGatewayKeyById(s, { id }));

  if (llmGatewayKey.id === "" || config === null) {
    return <Loading />;
  }

  return (
    <BoxGroup>
      <Box>
        <h1 className="text-lg text-gray-500 mb-4">Key Settings</h1>
        <KeySettingsForm
          llmGatewayKey={llmGatewayKey}
          orgSpendLimit={config.spendLimit}
        />
      </Box>

      {llmGatewayKey.status === "REVOKED" ? null : (
        <Box>
          <RevokeLlmGatewayKey llmGatewayKey={llmGatewayKey} />
        </Box>
      )}
    </BoxGroup>
  );
};
